import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import PropTypes from "prop-types";
import { fetchWorkcellMonthlyOee } from "../api";
import { formatHoursMinutes } from "../utils/time";
import { CircularProgress, Box } from "@mui/material";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const COLORS = ["#4a4a4a", "#43a047", "#006699"];

const WorkcellMonthlyOeeBarChart = ({ date, workcellId, onClick, loading: loadingProp, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatPercent01 = (value01) => {
    const num = Number(value01);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(num);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const callParent = (typeof onLoadingChange === 'function');
    callParent && onLoadingChange(true);
    if (!date || !workcellId) {
      if (callParent) onLoadingChange(false);
      return;
    }
    fetchWorkcellMonthlyOee(workcellId, date)
      .then((result) => {
        const days = getMonthDays(date);
        const fullRangeData = days.map((day) => ({
          day,
          oee: null,
          actual_hours: 0,
          scheduled_hours: 0,
        }));
        result.forEach((entry) => {
          const idx = fullRangeData.findIndex((d) => d.day === entry.date);
          if (idx !== -1) {
            fullRangeData[idx] = {
              ...fullRangeData[idx],
              oee: entry.oee,
              actual_hours: entry.actual_hours,
              scheduled_hours: entry.scheduled_hours,
            };
          }
        });
        setData(fullRangeData);
        setLoading(false);
        callParent && onLoadingChange(false);
      })
      .catch((err) => {
        setError(t("workcell.monthlyOverview.oee.failedToLoad"));
        setLoading(false);
        callParent && onLoadingChange(false);
      })
      .finally(() => {
        setLoading?.(false);
        callParent && onLoadingChange(false);
      });
  }, [date, workcellId]);

  const isLoading = typeof loadingProp === 'boolean' ? loadingProp : loading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div>{error}</div>;

  const handleBarClick = (data, index) => {
    if (onClick && data && data.activeLabel) {
      onClick(data.activeLabel);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const oee = payload.find((p) => p.dataKey === "oee");
      const actual_hours = payload[0]?.payload?.actual_hours;
      const scheduled_hours = payload[0]?.payload?.scheduled_hours || 0;
      return (
        <div className="custom-tooltip" style={{ background: "#fff", border: "1px solid #ccc", padding: 8 }}>
          <div>
            <b>{t("common.date")}:</b> {label}
          </div>
          {oee && (
            <div style={{ color: COLORS[0] }}>
              {t("workcell.monthlyOverview.oee.tooltip.oee")}: {oee.value != null ? formatPercent01(oee.value) : t("common.na")}
            </div>
          )}
          <div style={{ color: COLORS[1] }}>
            {t("workcell.monthlyOverview.oee.tooltip.actualHours")}: {formatHoursMinutes(actual_hours)}
          </div>
          <div style={{ color: COLORS[2] }}>
            {t("workcell.monthlyOverview.oee.tooltip.scheduledHours")}: {formatHoursMinutes(scheduled_hours)}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const date = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
          {date.toLocaleString(locale, { month: 'long' })}
        </text>
      </g>
    ) : null;
  };

  return (
    <div style={{ width: "100%", height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
          onClick={handleBarClick}
          syncId="workcellCharts"
        >
          {/* Main X axis: days */}
          <XAxis
            dataKey="day"
            tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
            height={30}
            axisLine={true}
            tickLine={true}
          />
          {/* Secondary X axis: month name */}
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            height={20}
            xAxisId="month"
            tick={MonthTick}
            interval={0}
            allowDataOverflow={true}
            orientation="bottom"
          />
          <YAxis yAxisId="left" label={{ value: t("workcell.monthlyOverview.oee.axisLabel"), angle: -90, position: "insideLeft" }} tickFormatter={v => v * 100} domain={[0, 1]} />
          <Tooltip content={<CustomTooltip />} />
          <Bar yAxisId="left" dataKey="oee" fill={COLORS[0]} name={t("workcell.monthlyOverview.oee.legend.oee")} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

WorkcellMonthlyOeeBarChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

export default WorkcellMonthlyOeeBarChart;
